import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useState, useMemo } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import PartyLoader from '@/components/workers/PartyLoader';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  ListItemSecondaryAction,
  Tooltip,
  Alert,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import api from '@/services/ApiService';
import { useRouteLoaderData } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';

const ContactSync = ({ open, onClose, setDisableLive }) => {
  const { me } = useRouteLoaderData('auth');
  const steps = ['Daten laden', 'Daten prüfen', 'Import bestätigen'];
  const [activeStep, setActiveStep] = useState(0);
  const [dataOrganizations, setDataOrganizations] = useState([]);
  const [ready, setReady] = useState(false);
  const [importing, setImporting] = useState(false);

  const contactCounts = useMemo(() => {
    const counts = new Map();
    if (dataOrganizations.length === 0) return counts;
    for (const org of dataOrganizations) {
      const orgId = org._id || org.id;
      counts.set(orgId, org.contacts?.length || 0);
    }
    return counts;
  }, [dataOrganizations]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSelectOrganization = (id) => {
    if (importing) return;
    setDataOrganizations((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const newSelectedState = !item.selected;
          return {
            ...item,
            selected: newSelectedState,
            importContacts: newSelectedState,
          };
        }
        return item;
      }),
    );
  };

  const handleData = (result) => {
    setDataOrganizations(
      result.map((item) => ({ ...item, selected: true, importContacts: true })),
    );
    if (activeStep === 0) setReady(true);
  };

  const handleSelectAllOrganizations = (event) => {
    const newSelectedState = event.target.checked;
    setDataOrganizations((prev) =>
      prev.map((item) => ({
        ...item,
        selected: newSelectedState,
        importContacts: newSelectedState,
      })),
    );
  };

  const handleToggleImportContacts = (id) => {
    setDataOrganizations((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, importContacts: !item.importContacts }
          : item,
      ),
    );
  };

  const handleSelectAllContacts = () => {
    const allEnabled = dataOrganizations.every((d) => d.importContacts);
    setDataOrganizations((prev) =>
      prev.map((item) => ({ ...item, importContacts: !allEnabled })),
    );
  };

  const createDatasource = async (recordId, ds, isNew) => {
    let exists = !isNew;
    if (!isNew) {
      const {
        data: { result: doesExist },
      } = await api.get(
        `/datasource?recordId=${recordId}&source=${ds.source}&sourceId=${ds.sourceId}${ds.sourceType ? `&sourceType=${ds.sourceType}` : ''}`,
      );
      exists = doesExist;
    }
    if (!exists || exists.length === 0) {
      await api.post(`/datasource`, {
        recordId,
        source: ds.source,
        sourceId: String(ds.sourceId),
        ...(ds.sourceType && { sourceType: ds.sourceType }),
        sharedWith: [
          {
            type: 'tenant',
            tenant: me.tenant,
            permissions: { read: true, update: true, delete: true },
          },
        ],
      });
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setDisableLive(true);
    try {
      const selectedOrgs = dataOrganizations
        .filter((d) => d.selected)
        .sort((a, b) => {
          // TODO: build full parentpartyid tree
          const aKey = a.parentPartyId == null ? 0 : 1;
          const bKey = b.parentPartyId == null ? 0 : 1;
          return aKey - bKey;
        });

      // 1 - Create Persons
      for (const org of selectedOrgs) {
        if (!org.importContacts || !org.contacts?.length) {
          continue;
        }
        for (const person of org.contacts) {
          try {
            const {
              id,
              selected,
              importContacts,
              assignedOrganizations,
              isCustomer,
              isSupplier,
              isLead,
              contacts,
              datasources,
              contact,
              primary,
              createdAt,
              updatedAt,
              ...personData
            } = person;

            let isNew = !person._id;
            if (!person._id) {
              const { data } = await api.post('/party', {
                ...personData,
                partyType: 'PERSON',
                leadStatus:
                  personData.leadStatus === 'NONE'
                    ? null
                    : personData.leadStatus || null,
                customer: !!isCustomer,
                supplier: !!isSupplier,
                addresses: personData.addresses?.map((a) => ({
                  city: a.city || '',
                  zipcode: a.zipcode || '',
                  street1: a.street1 || '',
                  street2: a.street2 || '',
                  countryCode: a.countryCode || '',
                  primeAddress: a.primeAddress || false,
                })),
                sharedWith: [
                  {
                    type: 'tenant',
                    tenant: me.tenant,
                    permissions: { read: true, update: true, delete: true },
                  },
                ],
              });
              person._id = data.insertedId;
            } else {
              await api.put(`/party/${person._id}`, {
                ...personData,
                partyType: 'PERSON',
                leadStatus:
                  personData.leadStatus === 'NONE'
                    ? null
                    : personData.leadStatus || null,
                customer: !!isCustomer,
                supplier: !!isSupplier,
                addresses: personData.addresses?.map((a) => ({
                  city: a.city || '',
                  zipcode: a.zipcode || '',
                  street1: a.street1 || '',
                  street2: a.street2 || '',
                  countryCode: a.countryCode || '',
                  primeAddress: a.primeAddress || false,
                })),
              });
            }

            if (datasources) {
              for (const ds of datasources) {
                await createDatasource(person._id, ds, isNew);
              }
            }
          } catch (e) {
            console.error('Creating person failed:', person, e);
          }
        }
      }

      // 2 - Create Organizations
      for (const o in selectedOrgs) {
        const org = selectedOrgs[o];
        try {
          const {
            id,
            selected,
            importContacts,
            assignedOrganizations,
            isCustomer,
            isSupplier,
            isLead,
            contacts,
            datasources,
            updatedAt,
            createdAt,
            ...orgData
          } = org;

          if (orgData.parentPartyId) {
            const parentOrg = selectedOrgs.find(
              (o) =>
                !!o.datasources?.find(
                  (ds) =>
                    ds.sourceId === orgData.parentPartyId &&
                    ds.source === datasources[0]?.source,
                ),
            );
            if (parentOrg && parentOrg._id) {
              orgData.parentPartyId = parentOrg._id;
            } else {
              selectedOrgs[o] = null;
              continue;
            }
          }

          const finalContactList = [];

          if (org.contacts?.length > 0) {
            for (const person of org.contacts) {
              if (person._id) {
                finalContactList.push({
                  id: person._id,
                  contact: person.contact,
                  primary: person.primary,
                });
              }
            }
          }

          orgData.contacts = finalContactList;

          let data;
          if (orgData?._id) {
            const { data: resData } = await api.put(`/party/${orgData._id}`, {
              ...orgData,
              leadStatus:
                orgData.leadStatus === 'NONE'
                  ? null
                  : orgData.leadStatus || null,
              customer: !!isCustomer,
              supplier: !!isSupplier,
              addresses: orgData.addresses?.map((a) => ({
                city: a.city || '',
                zipcode: a.zipcode || '',
                street1: a.street1 || '',
                street2: a.street2 || '',
                countryCode: a.countryCode || '',
                primeAddress: a.primeAddress || false,
              })),
            });
            data = resData;
          } else {
            const { data: resData } = await api.post('/party', {
              ...orgData,
              leadStatus:
                orgData.leadStatus === 'NONE'
                  ? null
                  : orgData.leadStatus || null,
              customer: !!isCustomer,
              supplier: !!isSupplier,
              addresses: orgData.addresses?.map((a) => ({
                city: a.city || '',
                zipcode: a.zipcode || '',
                street1: a.street1 || '',
                street2: a.street2 || '',
                countryCode: a.countryCode || '',
                primeAddress: a.primeAddress || false,
              })),
              sharedWith: [
                {
                  type: 'tenant',
                  tenant: me.tenant,
                  permissions: { read: true, update: true, delete: true },
                },
              ],
            });
            data = resData;
          }

          const newOrgId = data.insertedId || orgData._id;
          selectedOrgs[o] = { ...org, _id: newOrgId };

          if (datasources) {
            for (const ds of datasources) {
              await createDatasource(newOrgId, ds, !orgData?._id);
            }
          }
        } catch (e) {
          console.error('Creating organization failed:', org, e);
        }
      }

      setDisableLive(false);
      setImporting(false);
      setActiveStep(0);
      setDataOrganizations([]);
      setReady(false);
      onClose();
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      setImporting(false);
    }
  };

  const numSelectedOrgs = dataOrganizations.filter((d) => d.selected).length;
  const orgCount = dataOrganizations.length;
  const numPersonsToImport = dataOrganizations
    .filter((org) => org.selected && org.importContacts)
    .reduce((sum, org) => {
      const newContacts = org.contacts?.length || 0;
      return sum + newContacts;
    }, 0);

  return (
    <Dialog
      open={open}
      onClose={importing ? null : onClose}
      PaperProps={{ sx: { width: '500px', maxWidth: 'none' } }}
    >
      <DialogTitle>Stammdatenimport</DialogTitle>
      <DialogContent>
        <Box>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && <PartyLoader setResult={handleData} />}

          {activeStep === 1 && (
            <>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  mb: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 0.5,
                }}
              >
                <Checkbox
                  color="primary"
                  indeterminate={
                    numSelectedOrgs > 0 && numSelectedOrgs < orgCount
                  }
                  checked={orgCount > 0 && numSelectedOrgs === orgCount}
                  onChange={handleSelectAllOrganizations}
                />
                <ListItemText primary="Alle auswählen" />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={handleSelectAllContacts}
                    edge="end"
                    aria-label="toggle all contacts"
                  >
                    <GroupIcon
                      color={
                        dataOrganizations.every((d) => d.importContacts)
                          ? 'primary'
                          : 'action'
                      }
                    />
                  </IconButton>
                </ListItemSecondaryAction>
              </Box>
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {dataOrganizations.map((item) => {
                  const contactCount = contactCounts.get(item.id) || 0;
                  return (
                    <ListItem
                      key={item.id}
                      secondaryAction={
                        contactCount > 0 && (
                          <Tooltip
                            arrow
                            title={
                              <>
                                <Typography
                                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                                >
                                  Kontakte:
                                </Typography>
                                {item.contacts.map((contact) => (
                                  <Typography
                                    variant="body2"
                                    key={contact.id || contact._id}
                                  >
                                    {`${contact.firstName || ''} ${contact.lastName || ''}`.trim() || (
                                      <em>Unbenannt</em>
                                    )}
                                    {contact.primary &&
                                      ' (Hauptansprechpartner)'}
                                    {!contact.primary &&
                                      contact.contact &&
                                      ' (Ansprechpartner)'}
                                    {!contact.primary &&
                                      !contact.contact &&
                                      ' (Mitarbeiter)'}
                                  </Typography>
                                ))}
                              </>
                            }
                          >
                            <Button
                              startIcon={<GroupIcon />}
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                handleToggleImportContacts(item.id)
                              }
                              disabled={importing}
                              sx={{
                                minWidth: '120px',
                                color: item.importContacts
                                  ? 'primary.main'
                                  : 'text.secondary',
                                borderColor: item.importContacts
                                  ? 'primary.main'
                                  : 'text.secondary',
                                '&:hover': {
                                  borderColor: item.importContacts
                                    ? 'primary.dark'
                                    : 'text.secondary',
                                },
                              }}
                            >
                              {contactCount} Kontakt
                              {contactCount !== 1 ? 'e' : ''}
                            </Button>
                          </Tooltip>
                        )
                      }
                    >
                      <Checkbox
                        checked={item.selected || false}
                        onChange={() => handleSelectOrganization(item.id)}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={
                          item.company ||
                          (item.lastName
                            ? `${item.firstName || ''} ${item.lastName}`
                            : '<em>Unbekannt</em>')
                        }
                        secondary={item.customerNumber || item.supplierNumber}
                        title={item.company || 'Unbekannt'}
                        sx={{
                          m: 0,
                          maxWidth: '220px',
                          '& .MuiTypography-root': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {activeStep === 2 && (
            <Box sx={{ mt: 2 }}>
              <strong>Import bereit!</strong>
              <Box sx={{ mt: 1 }}>
                Bitte bestätigen Sie den Import von{' '}
                <strong>{numSelectedOrgs}</strong> Organisationen und{' '}
                <strong>{numPersonsToImport}</strong> Kontakten.
              </Box>
              {importing && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Schließen Sie das Fenster nicht, während der Import läuft!
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={activeStep === 0 || importing} onClick={handleBack}>
          Zurück
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={!ready}>
            Weiter
          </Button>
        ) : (
          <LoadingButton
            variant="contained"
            color="success"
            onClick={handleImport}
            loading={importing}
            disabled={importing}
          >
            Import bestätigen
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactSync;
